import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled/macro';
import {
  getCommentCountForFunction,
  getHasUnreadForFunction,
} from '../../store/comments/commentsSelectors';
import { receiveCommentCount } from '../../store/comments/commentsActions';
import { fetchCommentCount } from '../../store/api';
import { setObservationCommentsOpen } from '../../store/ui/uiActions';
import CommentIcon from './CommentIcon';
import CommentHoverPopover from './CommentHoverPopover';

const Container = styled.div`
  position: absolute;
  top: 50%;
  right: 10px;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  gap: 4px;
  z-index: 5;
  pointer-events: auto;
`;

const IconButton = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px;
  background-color: transparent;
  color: var(--color-white);
  cursor: pointer;
  transition: color 0.15s ease;
  &[data-state='unread'],
  &[data-state='active'] {
    color: var(--color-observations);
  }
  &:hover {
    color: var(--color-observations);
  }
`;

const CardCommentOverlay = ({ functionId }) => {
  const dispatch = useDispatch();
  const count = useSelector(getCommentCountForFunction(functionId));
  const unread = useSelector(getHasUnreadForFunction(functionId));
  const activeFunctionId = useSelector((state) => state.ui.uiObservationCommentsOpen);
  const active = activeFunctionId === functionId;
  const [hoverRect, setHoverRect] = useState(null);
  const iconRef = useRef(null);

  const state = active ? 'active' : unread ? 'unread' : 'read';

  useEffect(() => {
    let cancelled = false;
    fetchCommentCount(functionId)
      .then(({ response }) => {
        if (cancelled || !response) return;
        dispatch(receiveCommentCount({ functionId, total: response.total }));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [functionId, dispatch]);

  const handleMouseEnter = () => {
    if (iconRef.current) {
      setHoverRect(iconRef.current.getBoundingClientRect());
    }
  };

  const handleMessageClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (active) {
      dispatch(setObservationCommentsOpen(null));
      return;
    }
    const target = document.querySelector(`[data-id="${functionId}"]`);
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    dispatch(setObservationCommentsOpen(functionId));
  };

  return (
    <>
      <Container
        onMouseEnter={handleMouseEnter}
        onMouseLeave={() => setHoverRect(null)}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <IconButton
          role="button"
          tabIndex={0}
          ref={iconRef}
          data-state={state}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={handleMessageClick}
          title={count > 0 ? `${count} comment${count === 1 ? '' : 's'}` : 'Add comment'}
        >
          <CommentIcon width={16} height={16} filled={state === 'unread'} />
        </IconButton>
      </Container>
      <CommentHoverPopover functionId={functionId} anchorRect={hoverRect} />
    </>
  );
};

export default CardCommentOverlay;
